import { handleActions } from 'redux-actions';
import endpoints from '../../../config/endpoints';
import { formValueSelector } from 'redux-form';
import { LOCATION_CHANGE, replace } from 'react-router-redux';

import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import moment from 'moment';
import qs from 'qs';
import csv from 'csv-stringify';

import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import includes from 'lodash/includes';
import sumBy from 'lodash/sumBy';
import values from 'lodash/values';

import createFiltersForm from '../components/filters';
import createTransactionForm from '../components/forms/transaction';
import createPayoutForm from '../components/forms/create-payout';
import createFilters from '../../shared/filters';
import fraudPopup from '../components/popups/fraud';
import paymentDate from '../components/popups/payment-date';

import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';

import { formatMoney } from '../../shared/format';

import { createSelectOptionsFromSysEnums } from '../../shared/form-elements/select';
import { enum as payoutStatuses } from '../../../../../../enums/payout-statuses';
import { enum as payoutSystemCategories } from '../../../../../../enums/payout-system-categories';
import { actions as rootActions } from '../../root';

import convertDataToRows from './helpers/convert-data-to-rows';
import sortRows from './helpers/sort-rows';

const isEverad = process.env.CPA_ENV === 'everad';

const calendar = createCalendar({
	namespace: 'payoutsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'year').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filtersForm = createFiltersForm({
	namespace: 'payoutsList',
	stateNodeName: 'filtersForm'
});

const transactionForm = createTransactionForm({
	namespace: 'payoutsList',
	stateNodeName: 'transactionForm'
});

const payoutForm = createPayoutForm({
	namespace: 'payoutsList',
	stateNodeName: 'createPayoutForm',
	formName: 'payoutsListCreatePayoutForm'
});

const filters = createFilters({
	namespace: 'payoutsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [{
		name: 'affiliates',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'managers',
		title: 'Managers',
		placeholder: 'Add managers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.ADMINS
		}
	}]
});

// action types
const FETCH_DATA_SUCCESS = 'payouts/fetch-data-success';
const FETCH_DATA_ERROR = 'payouts/fetch-data-error';

const CHANGE_DIRECTION = 'payouts/change-direction';

const FETCH_PAYOUT_SYSTEMS_LIST_SUCCESS = 'payouts/fetch-payout-sys-list';

const FETCH_SUGGEST_SUCCESS = 'payouts/fetch-suggest-success';
const FETCH_SUGGEST_ERROR = 'payouts/fetch-suggest-error';

const TOGGLE_PROGRESS_BAR = 'payouts/toggle-progress-bar';
const TOGGLE_ACTIVE_CATEGORY = 'payouts/toggle-active-category';

const APPLY = 'payouts/apply';

const APPROVE_SUCCESS = 'payouts/approve-success';
const CONFIRM_SUCCESS = 'payouts/confirm-success';
const REJECT_SUCCESS = 'payouts/reject-success';
const PAYOUT_ACTION_ERROR = 'payouts/action-error';

const URGENCY_SUCCESS = 'payouts/urgency-success';
const URGENCY_ERROR = 'payouts/urgency-error';

const PROCESS_PAYOUTS = 'payouts/process-payouts';

const FETCH_BALANCE_SUCCESS = 'payouts/fetch-balance-success';
const FETCH_BALANCE_ERROR = 'payouts/fetch-balance-error';

const ON_ORDER_SORT_END = 'payouts/on-order-sort-end';
const SAVE_ORDER_SUCCESS = 'payouts/save-order-success';
const SAVE_ORDER_ERROR = 'payouts/save-order-error';

// permissions
export const allowedPermissions = {
	view: 'payouts_list',
	approve: 'payouts_approve',
	confirm: 'payouts_confirm',
	create: 'payouts_create',
	createTransaction: 'transactions_create',
	viewAffiliates: 'affiliates_list',
	viewBalances: 'payout_systems_get_balance',
	changePayoutSystemDisplayPriority: 'payout_systems_change_display_priority'
};

// initial state
const initialState = {
	data: null,
	systemBalances: {},
	calendar: calendar.initialState,
	filtersForm: filtersForm.initialState,
	transactionForm: transactionForm.initialState,
	createPayoutForm: payoutForm.initialState,
	selectedFilters: {
		statuses: [
			{ label: 'pending', value: 'pending' },
			{ label: 'approved', value: 'approved' },
			{ label: 'confirmed', value: 'confirmed' }
		]
	},
	payoutSystems: null,
	groupedPayoutSystems: null,
	activePayoutSystemId: null,
	activePayoutSystemCategories: [],
	showProgressBar: false,
	filters: filters.initialState,
	sorting: {
		index: 'is_urgent',
		direction: 'desc'
	},
	error: false,
	sortedPayoutSystemsList: [],
	...fraudPopup.initialState,
	...paymentDate.initialState
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const state = getState();
			const permissions = state.root.data.permissions;
			const { selectedFilters } = state.payoutsList;

			const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'payoutsList');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`payouts/?${qs.stringify(savedFilters)}`));
			}

			dispatch(actions.fetchPayoutSystems()).then(action => {
				if (action.type === FETCH_PAYOUT_SYSTEMS_LIST_SUCCESS) {
					if (permissions.indexOf('payout_systems_get_balance') > -1) {
						getPayoutSystemsBalance(getState, dispatch);
					}
					getData(selectedFilters, getState, dispatch, externalQuery);
				}
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		fetchPayoutSystems: () => dispatch => {
			return http({
				url: endpoints.PAYOUT_SYSTEMS.LIST,
				method: 'get',
				actions: [FETCH_PAYOUT_SYSTEMS_LIST_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		apply: () => (dispatch, getState) => {
			const state = getState();
			const { filters: { selectedFilters } } = state.payoutsList;
			const request = {
				statuses: formValueSelector('payoutsListForm')(state, 'statuses') || ''
			};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					if (filter.value.length) {
						request[filter.name] = filter.value;
					}
				});
			}

			dispatch({ type: TOGGLE_PROGRESS_BAR });

			dispatch({
				type: APPLY,
				payload: request
			});

			dispatch(actions.fetchPayoutSystems()).then(action => {
				if (action.type === FETCH_PAYOUT_SYSTEMS_LIST_SUCCESS) {
					getData(request, getState, dispatch);
				}
			});
		},

		clear: () => (dispatch, getState) => {
			dispatch(replace('/payouts/'));
		},

		export: () => (dispatch, getState) => {
			const state = getState();

			const data = selectors.getData(state);
			const payoutSystem = selectors.getActivePayoutSystem(state);

			if (!data || !data.length) return;

			const headers = [
				{ index: 'status', title: 'Status' },
				{ index: 'is_urgent', title: 'Urgency' },
				{ index: 'created_at', title: 'Created at' },
				{ index: 'affiliate', title: 'Affiliate' },
				{ index: 'amount', title: 'Amount' },
				{ index: 'currency', title: 'Currency' },
				{ index: 'amount_to_pay', title: 'To pay' },
				{ index: 'credentials', title: 'Credentials' },
				{ index: 'paid_at', title: 'Paid at' },
				{ index: 'paid', title: 'Paid' }
			];

			const rows = [headers.map((header) => header.title)];

			rows.push(...flatten(data.map((row) => {
				return row.payouts.map((payout) => {
					return headers.map((header) => {
						switch (header.index) {
							case 'status':
								return payout.status;
							case 'is_urgent':
								return payout.is_urgent ? 'urgent' : null;
							case 'created_at':
								return moment(payout.created_at).format('YYYY.MM.DD HH:mm');
							case 'affiliate':
								return row.affiliate.email;
							case 'amount':
								return formatMoney(payout.amount, payoutSystem.allow_floats);
							case 'currency':
								return payout.currency;
							case 'amount_to_pay':
								return formatMoney(payout.amount_to_pay, payoutSystem.allow_floats);
							case 'credentials':
								return values(row.affiliate.payout_settings.credentials).join(' / ');
							case 'paid_at':
								return payout.status === payoutStatuses.paid
									? moment(payout.paid_at || payout.updated_at).format('YYYY.MM.DD HH:mm')
									: null;
							case 'paid':
								return row.affiliate.paid_payouts_count;
						}
					});
				});
			})));

			csv(rows, (err, res) => {
				const filename = `Export--${payoutSystem.title}--${moment().format('YYYY-MM-DD--HH-mm-ss')}.csv`;
				const csvData = new Blob([res], { type: 'text/csv;charset=utf-8;' });
				// IE11 & Edge
				if (navigator.msSaveBlob) {
					navigator.msSaveBlob(csvData, filename);
				} else {
					// In FF link must be added to DOM to be clicked
					const link = document.createElement('a');
					link.href = window.URL.createObjectURL(csvData);
					link.setAttribute('download', filename);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				}
			});
		},

		affiliatesLookups: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		changePayoutSystem: id => (dispatch, getState) => {
			dispatch({ type: TOGGLE_PROGRESS_BAR });
			dispatch(rootActions.setLocalData('activePayoutSystemId', id));
			const { selectedFilters } = getState().payoutsList;

			dispatch(actions.fetchPayoutSystems()).then(action => {
				if (action.type === FETCH_PAYOUT_SYSTEMS_LIST_SUCCESS) {
					getData(selectedFilters, getState, dispatch);
				}
			});
		},

		updateSystemBalance: id => (dispatch, getState) => {
			http({
				url: [`${endpoints.PAYOUT_SYSTEMS.BALANCE_INFO.replace('{id}', id)}?force=1`],
				method: 'get',
				actions: [FETCH_BALANCE_SUCCESS, FETCH_BALANCE_ERROR],
				addToResponse: [id]
			})(dispatch);
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),
		toggleActiveCategory: category => dispatch => {
			dispatch({ type: TOGGLE_ACTIVE_CATEGORY, payload: category });
		},
		orderSortedEnd: (sorted, oldIndex, newIndex) => dispatch => {
			if (oldIndex === newIndex) return;
			dispatch({ type: ON_ORDER_SORT_END, payload: sorted });
			dispatch(actions.saveOrderSort());
		},

		saveOrderSort: () => (dispatch, getState) => {
			const request = http({
				url: endpoints.PAYOUT_SYSTEMS.SORT,
				method: 'post',
				actions: [SAVE_ORDER_SUCCESS, SAVE_ORDER_ERROR],
				data: {
					data: getState().payoutsList.sortedPayoutSystemsList
				}
			});

			dispatch(request).then(action => {
				if (action.type === SAVE_ORDER_SUCCESS) {
					dispatch(actions.fetchData());
				}
			});
		},

		sortColumn: (index) => (dispatch, getState) => {
			dispatch({
				type: CHANGE_DIRECTION,
				payload: { index }
			});
		},

		changeUrgency: (payout_setting_id, urgency) => (dispatch, getState) => {
			const request = http({
				url: endpoints.PAYOUTS.CHANGE_URGENCY
					.replace('{payout_setting_id}', payout_setting_id)
					.replace('{urgency}', urgency ? 'urgent' : 'not-urgent'),
				method: 'put',
				actions: [URGENCY_SUCCESS, URGENCY_ERROR],
				addToResponse: { payout_setting_id, urgency }
			});

			return request(dispatch);
		},

		approve: (selectedPayouts) => (dispatch) => {
			const payouts = selectedPayouts
				.filter((payout) => payout.status === payoutStatuses.pending);

			const payoutIds = payouts.map((payout) => payout.id);

			dispatch({ type: PROCESS_PAYOUTS, meta: { payoutIds } });

			const request = http({
				url: endpoints.PAYOUTS.APPROVE,
				method: 'put',
				data: { payoutIds },
				actions: [APPROVE_SUCCESS, PAYOUT_ACTION_ERROR],
				addToResponse: { payoutIds }
			});

			return request(dispatch);
		},

		confirm: (selectedPayouts, { partially = false } = {}) => (dispatch, getState) => {
			const payouts = selectedPayouts
				.filter((payout) => payout.status === payoutStatuses.approved);

			const payoutIds = payouts.map((payout) => payout.id);

			const totalAmount = sumBy(payouts, 'amount');

			let paid_at = null;

			let amount = totalAmount;

			const payoutSystem = selectors.getActivePayoutSystem(getState());

			if (payoutSystem.type === 'manual' && !selectors.getIsPaymentDatePopupVisible(getState())) {
				dispatch(actions.initPaymentDatePopup(selectedPayouts, partially));
				return ;
			}

			if (selectors.getIsPaymentDatePopupVisible(getState())) {
				dispatch(actions.togglePaymentDatePopup());
				paid_at = selectors.getPayedAt(getState());
			}

			if (partially) {

				amount = +prompt(`Enter amount (decimal numbers ${payoutSystem.allow_floats ? 'allowed' : 'not allowed'}):`);

				if (!amount) {
					return;
				}

				if (isNaN(amount) || amount < 0) {
					return alert('Incorrect amount');
				}

				if (!payoutSystem.allow_floats && !Number.isInteger(amount)) {
					return alert('Incorrect amount (decimal numbers not allowed)');
				}

				if (amount > totalAmount) {
					return alert('Incorrect amount (amount exceeds maximum possible value)');
				}
			}

			dispatch({ type: PROCESS_PAYOUTS, meta: { payoutIds } });

			const request = http({
				url: endpoints.PAYOUTS.CONFIRM,
				method: 'put',
				data: { payoutIds, amount, paid_at },
				actions: [CONFIRM_SUCCESS, PAYOUT_ACTION_ERROR],
				addToResponse: { payoutIds, partially, amount }
			});

			return request(dispatch);
		},

		reject: (selectedPayouts) => (dispatch, getState) => {

			return isEverad
				? dispatch(fraudPopup.actions.initFraudPopup(selectedPayouts))
				: dispatch(actions.rejectConfirmed(selectedPayouts));
		},

		rejectConfirmed: (selectedPayouts, params = {}) => (dispatch, getState) => {
			const { isFraud = null } = params;
			const payouts = selectedPayouts
				.filter((payout) => includes([payoutStatuses.pending, payoutStatuses.approved], payout.status));

			const payoutIds = payouts.map((payout) => payout.id);

			const amount = sumBy(payouts, 'amount');
			const payoutSystem = selectors.getActivePayoutSystem(getState());

			if (!confirm(`You are about to reject a payout of ${amount} ${payoutSystem.currency}. Are you sure?`)) {
				return;
			}

			dispatch({ type: PROCESS_PAYOUTS, meta: { payoutIds } });

			const request = http({
				url: `${endpoints.PAYOUTS.REJECT}?${qs.stringify({ isFraud })}`,
				method: 'put',
				data: { payoutIds },
				actions: [REJECT_SUCCESS, PAYOUT_ACTION_ERROR],
				addToResponse: { payoutIds }
			});

			return request(dispatch);
		}
	},
	calendar.actions,
	filtersForm.actions,
	transactionForm.actions,
	payoutForm.actions,
	fraudPopup.actions,
	paymentDate.actions,
	filters.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const data = map(action.payload.data, row => ({
				affiliate: row.affiliate,
				payouts: row.payouts.map((payout) => ({ ...payout, isInProcessing: false }))
			}));

			const rows = convertDataToRows(data);

			return {
				...state,
				data: sortRows(rows, state.sorting),
				activePayoutSystemId: action.meta.activePayoutSystemId,
				systemPayoutPercent: parseFloat(action.meta.systemPayoutPercent),
				showProgressBar: false,
				error: false
			};
		},
		[FETCH_PAYOUT_SYSTEMS_LIST_SUCCESS]: (state, action) => {
			let payoutSystems = action.payload.data;
			let groupedPayoutSystems = groupBy(orderBy(payoutSystems, 'display_priority', 'desc'), 'category');

			let groupedAndSortedPayoutSystems = sortCategories(groupedPayoutSystems, [
				payoutSystemCategories.digital_money,
				payoutSystemCategories.advertising_networks,
				payoutSystemCategories.banks,
				payoutSystemCategories.other,
				payoutSystemCategories.archive
			]);

			const activePayoutSystemCategories = groupedAndSortedPayoutSystems.map((item) => ({
				category: item.category,
				active: item.category !== payoutSystemCategories.archive
			}));

			return {
				...state,
				data: [],
				payoutSystems,
				groupedPayoutSystems: groupedAndSortedPayoutSystems,
				activePayoutSystemCategories,
				activePayoutSystemId: payoutSystems[0].id,
				showProgressBar: false
			};
		},
		[FETCH_BALANCE_SUCCESS]: (state, action) => {
			let updatedBalances = {};
			action.payload.forEach((res, index) => {
				if (!res.data) {
					return;
				}
				let systemId = action.meta[index];
				updatedBalances[systemId] = res.data;
			});
			return {...state, systemBalances: { ...state.systemBalances, ...updatedBalances }};
		},
		[APPLY]: (state, action) => {
			return {
				...state,
				selectedFilters: {
					...action.payload
				}
			};
		},
		[CHANGE_DIRECTION]: (state, action) => {
			const index = action.payload.index;

			const sorting = {
				index,
				direction: state.sorting.index === index
					? state.sorting.direction === 'desc' ? 'asc' : 'desc'
					: 'desc'
			};

			return {
				...state,
				data: sortRows(state.data, sorting),
				sorting
			};
		},
		[APPROVE_SUCCESS]: (state, action) => {
			const { payoutIds } = action.meta;

			return {
				...state,
				data: convertDataToRows(state.data.map((row) => ({
					...row,
					payouts: row.payouts.map((payout) => !includes(payoutIds, payout.id) ? payout : ({
						...payout,
						isInProcessing: false,
						status: payoutStatuses.approved
					}))
				})))
			};
		},
		[CONFIRM_SUCCESS]: (state, action) => {
			const { payoutIds, partially, amount } = action.meta;

			let unpaidAmount = amount;

			return {
				...state,
				data: convertDataToRows(state.data.map((row) => ({
					...row,
					// important: payouts must be sorted by created_at
					// this is necessary in order for payouts dividing to be the same as on server side
					payouts: row.payouts.reduce((payouts, payout) => {
						if (includes(payoutIds, payout.id)) {
							if (partially) {
								if (unpaidAmount >= payout.amount) { // payout is fully confirmed
									payouts.push({
										...payout,
										isInProcessing: false,
										status: payoutStatuses.confirmed
									});
								} else if (unpaidAmount > 0) { // payout is divided
									payouts.push({
										...payout,
										isInProcessing: false,
										amount: unpaidAmount,
										id: null,
										status: payoutStatuses.confirmed
									});
									payouts.push({
										...payout,
										isInProcessing: false,
										amount: payout.amount - unpaidAmount
									});
								} else { // insufficient amount to confirm remaining payouts
									payouts.push({
										...payout,
										isInProcessing: false
									});
								}

								unpaidAmount -= payout.amount;
							} else { // if confirm all
								payouts.push({
									...payout,
									isInProcessing: false,
									status: payoutStatuses.confirmed
								});
							}
						} else {
							payouts.push(payout);
						}

						return payouts;
					}, [])
				})))
			};
		},
		[REJECT_SUCCESS]: (state, action) => {
			const { payoutIds } = action.meta;

			return {
				...state,
				data: convertDataToRows(state.data.map((row) => ({
					...row,
					payouts: row.payouts.map((payout) => !includes(payoutIds, payout.id) ? payout : ({
						...payout,
						isInProcessing: false,
						status: payoutStatuses.rejected
					}))
				})))
			};
		},
		[PROCESS_PAYOUTS]: (state, action) => {
			const { payoutIds } = action.meta;

			return {
				...state,
				data: convertDataToRows(state.data.map((row) => ({
					...row,
					payouts: row.payouts.map((payout) => !includes(payoutIds, payout.id) ? payout : ({
						...payout,
						isInProcessing: true
					}))
				})))
			};
		},
		[PAYOUT_ACTION_ERROR]: (state, action) => {
			const error = get(action, 'payload.response.data.error')
				|| get(action, 'payload.response.data.message');

			if (error) alert(error);

			const { payoutIds } = action.meta;

			return {
				...state,
				data: convertDataToRows(state.data.map((row) => ({
					...row,
					payouts: row.payouts.map((payout) => !includes(payoutIds, payout.id) ? payout : ({
						...payout,
						isInProcessing: false
					}))
				})))
			};
		},
		[URGENCY_SUCCESS]: (state, action) => {
			const { payout_setting_id, urgency } = action.meta;

			return {
				...state,
				data: sortRows(convertDataToRows(state.data.map((row) => {
					return row.affiliate.payout_settings.id !== payout_setting_id ? row : ({
						...row,
						payouts: row.payouts.map((payout) => {
							return !includes([payoutStatuses.pending, payoutStatuses.approved], payout.status) ? payout : ({
								...payout,
								is_urgent: urgency
							});
						})
					});
				})), state.sorting)
			};
		},
		[URGENCY_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
		[FETCH_DATA_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
		[FETCH_SUGGEST_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[TOGGLE_ACTIVE_CATEGORY]: (state, action) => {
			const category = action.payload;
			const activePayoutSystemCategories = state.activePayoutSystemCategories;

			return {
				...state,
				activePayoutSystemCategories: activePayoutSystemCategories.map(item=> {
					if (item.category == category) {
						item.active = !item.active;
					}
					return item;
				})
			};
		},
		[ON_ORDER_SORT_END]: (state, actions) => ({
			...state,
			sortedPayoutSystemsList: actions.payload
		}),
		[SAVE_ORDER_SUCCESS]: state => ({
			...state,
			sortedPayoutSystemsList: []
		})
	},
	calendar.reducers,
	filtersForm.reducers,
	transactionForm.reducers,
	fraudPopup.reducers,
	paymentDate.reducers,
	payoutForm.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.payoutsList.data,
	getAdminEmail: state => state.adminProfile && state.root.data && state.root.data.email,
	getCalendar: state => calendar.selectors.getCalendar(state => state.payoutsList.calendar, state => state)(state),
	getFiltersFormData: filtersForm.selectors.getFormData,
	getTransactionFormData: transactionForm.selectors.getFormData,
	getTransactionShouldShowStatus: transactionForm.selectors.getShouldShowStatus,
	getCreatePayoutFormData: payoutForm.selectors.getFormData,
	getCreatePayoutShouldShowStatus: payoutForm.selectors.getShouldShowStatus,
	getPayoutSystems: state => state.payoutsList.payoutSystems,
	getGroupedPayoutSystems: state => state.payoutsList.groupedPayoutSystems,
	getSystemBalances: state => state.payoutsList.systemBalances,
	getPayoutSystemCategoriesList: state => state.root.lists['payout-system-categories'],
	getActivePayoutSystemCategories: state => state.payoutsList.activePayoutSystemCategories,
	getPayoutStatusesEnum: () => payoutStatuses,
	getActivePayoutSystemId: state => {
		const activePayoutSystemId = get(state.root.localData, 'activePayoutSystemId');

		return !isUndefined(activePayoutSystemId) && find(state.payoutsList.payoutSystems, { id: activePayoutSystemId })
			? activePayoutSystemId
			: state.payoutsList.activePayoutSystemId;
	},
	getActivePayoutSystem: state => {
		return find(state.payoutsList.payoutSystems, { id: selectors.getActivePayoutSystemId(state) });
	},
	getProgressBar: state => state.payoutsList.showProgressBar,
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getFilters: state => filters.selectors.getFilters(
		state => state.payoutsList.filters,
		state => state
	)(state),
	getSorting: state => state.payoutsList.sorting,
	getError: state => state.payoutsList.error,
	...fraudPopup.selectors,
	...paymentDate.selectors
};

function getData(selectedFilters, getState, dispatch, externalQuery) {
	const { affiliates, managers, statuses } = selectedFilters;
	const appliedFilters = {};

	const state = getState();
	const { payoutSystems, calendar } = state.payoutsList;
	const activePayoutSystemId = selectors.getActivePayoutSystemId(state);

	const activeSystem = payoutSystems && find(payoutSystems, {id: activePayoutSystemId});

	if (affiliates) {
		appliedFilters.affiliates = affiliates && affiliates.map(obj => obj.value);
	}

	if (managers) {
		appliedFilters.managers = managers.map(obj => obj.value);
	}

	if (statuses) {
		appliedFilters.statuses = statuses && statuses.map(obj => obj.value);
	}

	if (activeSystem) {
		appliedFilters.systems = activeSystem && [activeSystem.id];
	}

	if (externalQuery && externalQuery.query) {
		forEach(externalQuery.query, (value, key) => {
			appliedFilters[key] = value;
		});
	}

	const query = qs.stringify({
		...appliedFilters,
		date_range: getDateRangeFromCalendar(calendar)
	});

	const request = http({
		url: `${endpoints.PAYOUTS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		addToResponse: {activePayoutSystemId, systemPayoutPercent: activeSystem.payout_percent}
	});

	return dispatch(request);
}

function getPayoutSystemsBalance (getState, dispatch) {
	const state = getState();
	const { payoutSystems } = state.payoutsList;
	let urls = [];
	let ids = [];
	payoutSystems.forEach(system => {
		urls.push(endpoints.PAYOUT_SYSTEMS.BALANCE_INFO.replace('{id}', system.id));
		ids.push(system.id);
	});

	dispatch(actions.toggleProgressBar());

	http({
		url: urls,
		method: 'get',
		actions: [FETCH_BALANCE_SUCCESS, FETCH_BALANCE_ERROR],
		addToResponse: ids
	})(dispatch);
}

const sortCategories = (groupedObj, sortOrderArray) => {
	return sortOrderArray.map(item => {
		let paySystems = groupedObj[item] || [];
		return { category: item, paySystems };
	});
};


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payouts/list/index.js